<template>
  <article
    class="cr-row bq-row bq-row--selectable"
    :style="{ gridTemplateColumns: gridColumns }"
    role="listitem"
    tabindex="0"
    @click="openInquiry"
    @keydown.enter.prevent="openInquiry"
  >
    <div class="bq-row__select" @click.stop>
      <input
        type="checkbox"
        :checked="selected"
        :aria-label="`Select ${displayName}`"
        @change="$emit('toggle-select', query)"
      />
    </div>

    <div
      v-for="column in columns"
      :key="column.key"
      class="bq-row__field"
      :class="{
        'bq-row__field--name': column.key === 'contact_name',
        'bq-row__field--long': column.long,
      }"
    >
      <span class="cr-row__field-label">{{ column.label }}</span>

      <div v-if="column.key === 'contact_name'" class="bq-row__lead-main">
        <span class="bq-row__avatar" aria-hidden="true">{{ initials }}</span>
        <span class="bq-row__name" :title="fieldValue(column) || ''">
          {{ fieldValue(column) || '—' }}
        </span>
        <span
          v-if="query.leadScore != null"
          class="bq-row__score"
          :class="`bq-row__score--${scoreTone}`"
          :title="query.leadOverview || 'AI lead score'"
        >
          {{ query.leadScore }}
        </span>
      </div>

      <span
        v-else
        class="bq-row__field-value"
        :class="{ 'bq-row__field-value--long': column.long }"
        :title="fieldValue(column) || ''"
      >
        {{ fieldValue(column) || '—' }}
      </span>
    </div>

    <div class="bq-row__status">
      <span class="cr-row__field-label">Status</span>
      <span
        class="bq-status"
        :class="`bq-status--${query.statusTone || 'neutral'}`"
      >
        {{ query.statusLabel }}
      </span>
      <span
        v-if="query.disabled"
        class="bq-status bq-status--neutral"
        title="Lead is disabled"
      >
        Disabled
      </span>
      <span
        v-if="query.hasCallbackRequested"
        class="bq-status bq-status--warning"
        :title="query.callbackNote || `Call back on ${query.callbackRequestedOnLabel}`"
      >
        <v-icon icon="mdi-phone-clock-outline" size="14" start />
        Callback {{ query.callbackRequestedOnLabel }}
      </span>
    </div>

    <div class="bq-row__submitted">
      <span class="cr-row__field-label">Submitted</span>
      <span class="bq-row__date">{{ query.createdOnLabel }}</span>
      <span v-if="query.source" class="bq-row__source">{{ query.source }}</span>
    </div>

    <div class="bq-row__actions" @click.stop>
      <span class="cr-row__field-label">Actions</span>
      <v-menu location="bottom end" offset="8" :close-on-content-click="true">
        <template #activator="{ props: menuProps }">
          <button
            type="button"
            class="bq-actions-trigger"
            aria-label="Business query actions"
            v-bind="menuProps"
            @click.stop
          >
            <v-icon icon="mdi-dots-vertical" size="18" />
          </button>
        </template>

        <div class="bq-actions-menu" role="menu">
          <button
            type="button"
            class="bq-actions-menu__item"
            role="menuitem"
            @click="$emit('view-inquiry', query)"
          >
            <span class="bq-actions-menu__icon bq-actions-menu__icon--view">
              <v-icon icon="mdi-eye-outline" size="18" />
            </span>
            <span class="bq-actions-menu__text">
              <span class="bq-actions-menu__title">View</span>
              <span class="bq-actions-menu__desc">Open lead profile</span>
            </span>
          </button>

          <button
            type="button"
            class="bq-actions-menu__item"
            role="menuitem"
            :disabled="!query.requestId"
            @click="$emit('edit', query)"
          >
            <span class="bq-actions-menu__icon bq-actions-menu__icon--contacted">
              <v-icon icon="mdi-pencil-outline" size="18" />
            </span>
            <span class="bq-actions-menu__text">
              <span class="bq-actions-menu__title">Edit</span>
              <span class="bq-actions-menu__desc">Update lead details</span>
            </span>
          </button>

          <button
            v-if="query.disabled"
            type="button"
            class="bq-actions-menu__item"
            role="menuitem"
            :disabled="!query.requestId"
            @click="$emit('enable', query)"
          >
            <span class="bq-actions-menu__icon bq-actions-menu__icon--converted">
              <v-icon icon="mdi-play-circle-outline" size="18" />
            </span>
            <span class="bq-actions-menu__text">
              <span class="bq-actions-menu__title">Enable</span>
              <span class="bq-actions-menu__desc">Make this lead active again</span>
            </span>
          </button>

          <button
            v-else
            type="button"
            class="bq-actions-menu__item"
            role="menuitem"
            :disabled="!query.requestId"
            @click="$emit('disable', query)"
          >
            <span class="bq-actions-menu__icon bq-actions-menu__icon--pending">
              <v-icon icon="mdi-pause-circle-outline" size="18" />
            </span>
            <span class="bq-actions-menu__text">
              <span class="bq-actions-menu__title">Disable</span>
              <span class="bq-actions-menu__desc">Mark this lead as inactive</span>
            </span>
          </button>

          <button
            type="button"
            class="bq-actions-menu__item bq-actions-menu__item--danger"
            role="menuitem"
            :disabled="!query.requestId"
            @click="$emit('delete', query)"
          >
            <span class="bq-actions-menu__icon bq-actions-menu__icon--rejected">
              <v-icon icon="mdi-trash-can-outline" size="18" />
            </span>
            <span class="bq-actions-menu__text">
              <span class="bq-actions-menu__title">Delete</span>
              <span class="bq-actions-menu__desc">Remove this lead permanently</span>
            </span>
          </button>
        </div>
      </v-menu>
    </div>
  </article>
</template>

<script setup>
import { computed } from 'vue'
import {
  businessQueryInitials,
  leadScoreTone,
} from '@/utils/businessQueries'
import { resolveLeadFieldDisplay, resolveLeadFieldValue } from '@/utils/leadFields'

defineOptions({ name: 'BusinessQueriesRow' })

const props = defineProps({
  query: { type: Object, required: true },
  selected: { type: Boolean, default: false },
  columns: { type: Array, default: () => [] },
  gridColumns: { type: String, default: '' },
  // Kept for parent compatibility; status actions removed from the menu.
  statusOptions: { type: Array, default: () => [] },
})

const emit = defineEmits([
  'view-inquiry',
  'edit',
  'enable',
  'disable',
  'delete',
  'toggle-select',
])

const displayName = computed(
  () =>
    resolveLeadFieldValue(props.query, 'contact_name') ||
    props.query.name ||
    'Lead',
)
const initials = computed(() => businessQueryInitials(displayName.value))
const scoreTone = computed(() => leadScoreTone(props.query.leadScore) || 'neutral')

function fieldValue(column) {
  return resolveLeadFieldDisplay(props.query, column.key, column.type)
}

function openInquiry() {
  emit('view-inquiry', props.query)
}
</script>
